import { useState, useMemo } from 'react'

/**
 * Custom hook for managing system maintenance and mandatory alert messages.
 * 
 * Filters maintenance messages by audience ('cxid-reg') and type, then manages visibility state.
 * System Maintenance messages can be dismissed by the user, while Mandatory Alert
 * messages cannot be dismissed and must remain visible.
 * 
 * @param {Array<Object>} maintenanceMessages - Array of maintenance message objects
 * @param {number} maintenanceMessages[].id - Unique identifier for the message
 * @param {string} maintenanceMessages[].type - Message type ('System Maintenance' or 'Mandatory Alert')
 * @param {string} maintenanceMessages[].text - Message text content to display
 * @param {string} maintenanceMessages[].audience - Target audience for the message (must be 'cxid-reg' to display)
 * @param {boolean} maintenanceMessages[].enabled - Whether the message is enabled
 * @param {string} maintenanceMessages[].startDateTime - ISO 8601 timestamp for message start
 * @param {string} maintenanceMessages[].endDateTime - ISO 8601 timestamp for message end
 * @param {string} maintenanceMessages[].version - Version identifier for the message
 * @param {null} maintenanceMessages[].userId - User-specific identifier (null for all users)
 * 
 * @returns {Object} Message state and control object
 * @returns {boolean} hasMessage - Whether a valid maintenance/alert message exists after filtering
 * @returns {boolean} isSystemMessageVisible - Whether the message should be displayed (not hidden by user)
 * @returns {Object|null} maintenanceMessage - The filtered maintenance message object or null
 * @returns {boolean} shouldSystemMessageBeDismissable - Whether the message can be dismissed (true only for 'System Maintenance' type)
 * @returns {Function} setHasSystemMessageBeenHidden - Function to toggle message visibility (only works for dismissable messages)
 * 
 * @example
 * const { maintenanceMessage, isSystemMessageVisible, shouldSystemMessageBeDismissable, setHasSystemMessageBeenHidden } = useSystemMessage(messages)
 */
export default function useSystemMessage(maintenanceMessages) {
  const [hasSystemMessageBeenHidden, setHasSystemMessageBeenHidden] = useState(false)

  /**
   * Find the first message with type 'System Maintenance' or 'Mandatory Alert'
   * Returns null if no matching message is found
   */
  const maintenanceMessage = useMemo(() => {
    return maintenanceMessages?.filter(msg => msg.audience === "cxid-reg" ).find(msg => msg.type === 'System Maintenance' || msg.type === 'Mandatory Alert') || null
  }, [maintenanceMessages]);

  /**
   * Determines if the message should be visible based on:
   * - Message exists
   * - Message has not been dismissed by the user
   */
  const isSystemMessageVisible = useMemo(() => {
    return maintenanceMessage && !hasSystemMessageBeenHidden
  }, [maintenanceMessage, hasSystemMessageBeenHidden]);

  /**
   * Only System Maintenance messages can be dismissed
   * Mandatory Alert messages are always non-dismissable
   */
  const shouldSystemMessageBeDismissable = useMemo(() => {
    return maintenanceMessage && maintenanceMessage.type === 'System Maintenance'
  }, [maintenanceMessage]);

  const systemMessageType = useMemo(() => {
    switch (maintenanceMessage?.type) {
      case 'System Maintenance':
        return 'info'
      case 'Mandatory Alert':
        return 'warning'
      default:
        return 'info'
    }
  }, [maintenanceMessage]);


  return { hasMessage: Boolean(maintenanceMessage), systemMessageType, isSystemMessageVisible, maintenanceMessage, shouldSystemMessageBeDismissable,  setHasSystemMessageBeenHidden}
}
